import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { History, Search, RefreshCw } from 'lucide-react';

// Colores e íconos por tipo de acción
const ACCION_COLOR = {
  crear:     { bg: 'var(--accent-dim)', fg: 'var(--accent)' },
  editar:    { bg: 'var(--bg-input)',   fg: 'var(--text-secondary)' },
  eliminar:  { bg: 'var(--red-dim, rgba(220,80,80,0.15))', fg: 'var(--red, #e06666)' },
  cancelar:  { bg: 'var(--amber-dim)',  fg: 'var(--amber)' },
  renovar:   { bg: 'var(--accent-dim)', fg: 'var(--accent)' },
  completar: { bg: 'var(--accent-dim)', fg: 'var(--accent)' },
};

const ACCION_LABEL = {
  crear: 'creó', editar: 'editó', eliminar: 'eliminó',
  cancelar: 'canceló', renovar: 'renovó', completar: 'completó',
};

function tiempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const ahora = new Date();
  const seg = Math.floor((ahora - d) / 1000);
  if (seg < 60) return 'hace instantes';
  if (seg < 3600) return `hace ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `hace ${Math.floor(seg / 3600)} h`;
  const dias = Math.floor(seg / 86400);
  if (dias < 7) return `hace ${dias} día${dias > 1 ? 's' : ''}`;
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fechaCompleta(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-UY', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function ActivityPage() {
  const { fetchAudit, showToast } = useApp();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroUser, setFiltroUser] = useState('todos');
  const [filtroAccion, setFiltroAccion] = useState('todas');
  const [busqueda, setBusqueda] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await fetchAudit(500);
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast('Error cargando actividad: ' + e.message, 'error');
    }
    setLoading(false);
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const usuarios = useMemo(
    () => ['todos', ...Array.from(new Set(logs.map(l => l.userName).filter(Boolean)))],
    [logs]
  );
  const acciones = useMemo(
    () => ['todas', ...Array.from(new Set(logs.map(l => l.action).filter(Boolean)))],
    [logs]
  );

  const filtrados = useMemo(() => logs.filter(l => {
    if (filtroUser !== 'todos' && l.userName !== filtroUser) return false;
    if (filtroAccion !== 'todas' && l.action !== filtroAccion) return false;
    if (busqueda) {
      const t = `${l.userName} ${l.action} ${l.entity} ${l.label}`.toLowerCase();
      if (!t.includes(busqueda.toLowerCase())) return false;
    }
    return true;
  }), [logs, filtroUser, filtroAccion, busqueda]);

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--amber-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <History size={20} color="var(--amber)" />
          </div>
          <div>
            <h1 className="page-title">Actividad</h1>
            <p className="page-subtitle">Registro de quién hizo qué y cuándo · {filtrados.length} eventos</p>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={cargar} disabled={loading}>
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, póliza, cliente..."
            style={{ width: '100%', paddingLeft: 32, fontSize: 13 }}
          />
        </div>
        <select value={filtroUser} onChange={e => setFiltroUser(e.target.value)} style={{ fontSize: 13 }}>
          {usuarios.map(u => <option key={u} value={u}>{u === 'todos' ? 'Todos los usuarios' : u}</option>)}
        </select>
        <select value={filtroAccion} onChange={e => setFiltroAccion(e.target.value)} style={{ fontSize: 13 }}>
          {acciones.map(a => <option key={a} value={a}>{a === 'todas' ? 'Todas las acciones' : (ACCION_LABEL[a] || a)}</option>)}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20 }}>Cargando...</p>
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}>
          <History size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
          <p style={{ fontSize: 13 }}>No hay actividad registrada todavía.</p>
          <p style={{ fontSize: 12, marginTop: 4 }}>Las acciones (crear, editar, eliminar) aparecerán acá a medida que ocurran.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--border)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
          {filtrados.map((l) => {
            const color = ACCION_COLOR[l.action] || ACCION_COLOR.editar;
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--bg-card)' }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: color.bg, color: color.fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-display)',
                }}>
                  {(l.userName || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                    <strong>{l.userName}</strong>{' '}
                    <span style={{ color: color.fg, fontWeight: 600 }}>{ACCION_LABEL[l.action] || l.action}</span>{' '}
                    {l.entity}{' '}
                    {l.label && <strong style={{ color: 'var(--text-secondary)' }}>{l.label}</strong>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }} title={fechaCompleta(l.at)}>
                    {tiempoRelativo(l.at)} · {fechaCompleta(l.at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
